import ExportButton from "@components/ExportButton";
import { FilterBar, FilterRow } from "@components/FilterBar";
import ObjectSelect from "@components/ObjectSelect";
import { useObjects } from "@features/devices/api";
import { PageLayoutWithHelp } from "@features/page-help";
import { type EventLogEntry, useEventLog, useEventTypes } from "@features/reports/api";
import { buildExportUrl } from "@shared/buildExportUrl";
import { useGlobalObject } from "@shared/hooks/useGlobalObject";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { useDefaultEmail } from "@shared/useDefaultEmail";
import { useMailComposer } from "@shared/useMailComposer";
import { useStandardPagination } from "@shared/useStandardPagination";
import { Alert, Input, Select, Space, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";

const ALL_EVENTS_VALUE = "ALL";

const ReportsEventsPage = () => {
  const objectsQuery = useObjects();
  const objects = objectsQuery.data ?? [];
  const typesQuery = useEventTypes();
  const { selectedObject, setSelectedObject } = useGlobalObject(objects);
  const [meterSearch, setMeterSearch] = useState("");
  const [selectedEvents, setSelectedEvents] = useState<string[]>([ALL_EVENTS_VALUE]);
  const [eventsInitialized, setEventsInitialized] = useState(false);

  const eventTypes = useMemo(() => typesQuery.data ?? [], [typesQuery.data]);
  const eventOptions = useMemo(() => {
    const base = [
      {
        label: (
          <Space size="small">
            <Tag color="blue">Все</Tag>
            <span>Все события</span>
          </Space>
        ),
        value: ALL_EVENTS_VALUE,
      },
    ];
    const rest = eventTypes.map((evt) => ({
      label: (
        <Space size="small">
          <Tag>{evt.code}</Tag>
          <span>{evt.name}</span>
        </Space>
      ),
      value: evt.code.toString(),
    }));
    return [...base, ...rest];
  }, [eventTypes]);

  useEffect(() => {
    if (!eventsInitialized && eventTypes.length > 0) {
      setSelectedEvents([ALL_EVENTS_VALUE]);
      setEventsInitialized(true);
    }
  }, [eventTypes, eventsInitialized]);

  const regionID = selectedObject?.regionId;
  const objectID = selectedObject?.objectId;

  const eventFilter = selectedEvents.includes(ALL_EVENTS_VALUE)
    ? undefined
    : selectedEvents.map((value) => Number(value));

  const eventsQuery = useEventLog(regionID, objectID, eventFilter, meterSearch || undefined);
  const data = eventsQuery.data ?? [];
  const pagination = useStandardPagination(data.length);

  const columns: ColumnsType<EventLogEntry> = [
    {
      title: "Фидер",
      dataIndex: "feeder_name",
      ellipsis: true,
      render: (value: string, row) => value || row.dispatcher_name || "—",
    },
    { title: "Счётчик №", dataIndex: "meter_number", width: 120 },
    { title: "Название", dataIndex: "channel_label", ellipsis: true },
    {
      title: "Время события",
      dataIndex: "event_time",
      width: 180,
      render: (value: string) => dayjs(value).format("DD.MM.YYYY HH:mm:ss"),
    },
    {
      title: "Начало",
      dataIndex: "event_start",
      width: 180,
      render: (value?: string) => (value ? dayjs(value).format("DD.MM.YYYY HH:mm:ss") : "—"),
    },
    {
      title: "Код",
      dataIndex: "event_code",
      width: 120,
      render: (value: number) => <Tag color="purple">{value}</Tag>,
    },
    {
      title: "Событие",
      dataIndex: "event_name",
      ellipsis: true,
      render: (value?: string) => value ?? "—",
    },
  ];

  const ready = regionID !== undefined && objectID !== undefined;
  const eventsParam = eventFilter ? eventFilter.join(",") : undefined;
  const exportUrl = ready
    ? buildExportUrl("/api/reports/events/logs", {
        region: regionID,
        object: objectID,
        meter: meterSearch.trim() || undefined,
        events: eventsParam,
      })
    : undefined;
  const currentObject = useMemo(
    () => objects.find((obj) => obj.n_ob === objectID && obj.syb_rnk === regionID),
    [objects, objectID, regionID],
  );
  const defaultEmail = useDefaultEmail();
  const { openComposer, mailModal, sendDisabled } = useMailComposer({ defaultEmail, exportUrl });
  const meterLabel = meterSearch.trim() ? meterSearch.trim() : "Все счётчики";
  const eventsLabel = selectedEvents.includes(ALL_EVENTS_VALUE)
    ? "Все события"
    : selectedEvents.join(", ");

  const handleSendMail = useCallback(() => {
    if (!ready) {
      return;
    }
    const objectLabel = currentObject?.txt
      ? `${currentObject.txt} (№${currentObject.n_ob})`
      : objectID
        ? `Объект №${objectID}`
        : "Объект";
    openComposer({
      subject: `События счётчиков — ${objectLabel}`,
      body: `Отчёт: Журнал событий\nОбъект: ${objectLabel}\nФильтр по счётчикам: ${meterLabel}\nТипы событий: ${eventsLabel}\n\nПрикреплён XLSX-файл с деталями.`,
      filename: `events_${regionID ?? "r"}_${objectID ?? "obj"}_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`,
    });
  }, [ready, currentObject, objectID, regionID, meterLabel, eventsLabel, openComposer]);

  return (
    <PageLayoutWithHelp
      title="События на счётчиках объекта"
      extra={<ExportButton url={exportUrl} onSend={handleSendMail} sendDisabled={sendDisabled} />}
    >
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <FilterBar>
          <FilterRow>
            <ObjectSelect
              objects={objects}
              loading={objectsQuery.isLoading}
              value={selectedObject}
              onChange={(value) => setSelectedObject(value)}
              allowClear
              style={{ minWidth: 320, flex: 1 }}
            />
            <Input
              placeholder="Поиск по номеру/серийному"
              style={{ width: 260 }}
              value={meterSearch}
              onChange={(e) => setMeterSearch(e.target.value)}
              allowClear
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="Фильтр по событиям"
              style={{ minWidth: 320 }}
              options={eventOptions}
              loading={typesQuery.isLoading}
              value={selectedEvents}
              onChange={(values) => {
                if (values.includes(ALL_EVENTS_VALUE) && values.length > 1) {
                  setSelectedEvents([ALL_EVENTS_VALUE]);
                  return;
                }
                if (values.length === 0) {
                  setSelectedEvents([]);
                  return;
                }
                setSelectedEvents(values.map((value) => value.toString()));
              }}
              optionLabelProp="value"
              maxTagCount="responsive"
            />
          </FilterRow>
        </FilterBar>
        {!ready && (
          <Alert
            type="info"
            showIcon
            title="Сначала выберите объект, чтобы загрузить события."
            style={{ maxWidth: 600 }}
          />
        )}
        {ready && (
          <EnterpriseTable<EventLogEntry>
            rowKey={(row) => `${row.meter_number}-${row.event_time}-${row.event_code}`}
            columns={columns}
            dataSource={data}
            loading={eventsQuery.isLoading}
            scroll={{ x: 1000 }}
            size="small"
            pagination={pagination}
            toolbar={{ reload: true, onReload: () => eventsQuery.refetch() }}
          />
        )}
        {mailModal}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default ReportsEventsPage;
